import React, { useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { Container, Draggable } from 'react-smooth-dnd'
import Carousel from 'react-material-ui-carousel'
import { Choice, Task } from 'models'
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  useMediaQuery,
} from '@mui/material'
import { theme } from 'config'
import { IColumn, IChoice } from 'types'

// DraggableChoice and DraggableColumn types
export interface DraggableChoice extends IChoice {
  index: number
  from?: 'answers' | 'column'
  userColumn?: number
  error?: boolean
  assigned?: boolean
}

// Extend IColumn with answers array.
export interface DraggableColumn extends IColumn {
  answers: DraggableChoice[]
}

export interface DropResult<T = unknown> {
  removedIndex: number | null
  addedIndex: number | null
  payload: T
  sourceIndex?: number
}

// Initialize columns and available choices from the task.
// All choices start in the available pool.
const initializeColumnsAndChoices = (task: Task) => {
  const columns: DraggableColumn[] = (task.columns || []).map((col) => ({
    ...col,
    answers: [] as DraggableChoice[],
  }))
  const availableChoices: DraggableChoice[] = (task.choices || []).map(
    (choice, idx) => ({
      ...choice,
      index: idx + 1,
      assigned: false,
    })
  )
  return { columns, availableChoices }
}

export interface IColumnTaskProps {
  task: Task
  minCorrect?: number
  maxCorrect?: number
  onAnswer?: (choice: Choice, columnId: number | string) => void
  onCorrect?: (correctAnswers: number) => void
  onIncorrect?: (inCorrectAnswers: number) => void
  onAllAnswered?: (
    totalAnswers: number,
    correctAnswers: number,
    inCorrectAnswers: number
  ) => void
  onSelect?: (
    columns: DraggableColumn[],
    availableChoices: DraggableChoice[]
  ) => void
  setAreColumnAnswersChecked: (checked: boolean) => void
}

export const ColumnTask = inject(
  'authStore',
  'routingStore'
)(
  observer((props: IColumnTaskProps): JSX.Element => {
    const {
      task,
      onAnswer,
      onCorrect,
      onIncorrect,
      onSelect,
      minCorrect,
      maxCorrect,
      onAllAnswered,
      setAreColumnAnswersChecked,
    } = props
    const { t } = useTranslation()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    // Capture initial state.
    const {
      columns: initialColumns,
      availableChoices: initialAvailableChoices,
    } = initializeColumnsAndChoices(task)

    // Keep the original total count constant.
    const totalQuestions = initialAvailableChoices.length

    const [columns, setColumns] = useState<DraggableColumn[]>(initialColumns)
    const [choices, setChoices] = useState<DraggableChoice[]>(
      initialAvailableChoices
    )
    const [currentQuestion, setCurrentQuestion] = useState(0)
    const [processedStatements, setProcessedStatements] = useState<number>(1)
    const [hoveredColumn, setHoveredColumn] = useState<number | null>(null)
    const [feedbackMessage, setFeedbackMessage] = useState<string>('')
    const [isChecked, setIsChecked] = useState<boolean>(false)
    const [correctCount, setCorrectCount] = useState(0)
    const [wrongCount, setWrongCount] = useState(0)
    const [selectedChoice, setSelectedChoice] =
      useState<DraggableChoice | null>(null)
    const [selectedColumnChoice, setSelectedColumnChoice] = useState<{
      columnIndex: number
      choiceIndex: number
    } | null>(null)

    // Handle native drop from the carousel.
    const handleNativeDrop = (
      columnIndex: number,
      e: React.DragEvent<HTMLDivElement>
    ) => {
      e.preventDefault()
      const data = e.dataTransfer.getData('choice')
      if (data) {
        const draggedChoice: DraggableChoice = JSON.parse(data)
        const originalCurrentQuestion = currentQuestion
        // Remove the choice from available choices.
        const updatedChoices = choices.filter(
          (choice) => choice.id !== draggedChoice.id
        )
        setChoices(updatedChoices)

        // Add the choice to the targeted column.
        const newColumns = [...columns]
        newColumns[columnIndex].answers = [
          ...newColumns[columnIndex].answers,
          {
            ...draggedChoice,
            userColumn: newColumns[columnIndex].index,
            error: false,
          },
        ]
        setColumns(newColumns)

        if (onAnswer) {
          onAnswer(
            draggedChoice as unknown as Choice,
            newColumns[columnIndex].index
          )
        }
        if (onSelect) {
          onSelect(newColumns, updatedChoices)
        }

        if (updatedChoices.length === 0) {
          setCurrentQuestion(0)
          setProcessedStatements(-1)
        } else {
          setCurrentQuestion(
            Math.min(originalCurrentQuestion, updatedChoices.length - 1)
          )
          setProcessedStatements(totalQuestions - updatedChoices.length + 1)
        }
      }
    }

    // Modify column tap handler to work with selected column choice
    const columnChangeMobile = (columnIndex: number) => {
      // If a column choice is already selected
      if (selectedColumnChoice !== null) {
        // Get the selected choice
        const sourceColumn = columns[selectedColumnChoice.columnIndex]
        const choice = sourceColumn.answers[selectedColumnChoice.choiceIndex]

        // Remove choice from source column
        const newColumns = [...columns]
        newColumns[selectedColumnChoice.columnIndex].answers = newColumns[
          selectedColumnChoice.columnIndex
        ].answers.filter(
          (_, index) => index !== selectedColumnChoice.choiceIndex
        )

        // Add choice to target column
        newColumns[columnIndex].answers.push({
          ...choice,
          userColumn: newColumns[columnIndex].index,
          error: false,
        })

        // Reset selected choice
        setSelectedColumnChoice(null)
        setColumns(newColumns)

        // Trigger callbacks
        if (onAnswer) {
          onAnswer(choice as unknown as Choice, newColumns[columnIndex].index)
        }
        if (onSelect) {
          onSelect(newColumns, choices)
        }
      } else if (selectedChoice) {
        const originalCurrentQuestion = currentQuestion
        // Existing logic for dropping from carousel
        const updatedChoices = choices.filter((c) => c.id !== selectedChoice.id)
        const updatedColumns = [...columns]
        updatedColumns[columnIndex].answers.push({
          ...selectedChoice,
          userColumn: updatedColumns[columnIndex].index,
          error: false,
        })
        setColumns(updatedColumns)
        setSelectedChoice(null)
        setChoices(updatedChoices)

        if (onAnswer) {
          onAnswer(
            selectedChoice as unknown as Choice,
            updatedColumns[columnIndex].index
          )
        }
        if (onSelect) {
          onSelect(updatedColumns, updatedChoices)
        }

        if (updatedChoices.length === 0) {
          setCurrentQuestion(0)
          setProcessedStatements(-1)
        } else {
          setCurrentQuestion(
            Math.min(originalCurrentQuestion, updatedChoices.length - 1)
          )
          setProcessedStatements(totalQuestions - updatedChoices.length + 1)
        }
      }
    }

    // Handle drop events from react-smooth-dnd within a column.
    const columnChangeDesktop = (
      columnIndex: number,
      dropResult: DropResult<DraggableChoice>
    ) => {
      const newColumns = [...columns]
      const column = newColumns[columnIndex]
      const newAnswersForColumn = applyDrag<DraggableChoice>(
        column.answers,
        dropResult
      )
      if (dropResult.addedIndex !== null) {
        newAnswersForColumn[dropResult.addedIndex] = {
          ...newAnswersForColumn[dropResult.addedIndex],
          userColumn: column.index,
          error: false,
        }
        if (onAnswer) {
          onAnswer(
            newAnswersForColumn[dropResult.addedIndex] as unknown as Choice,
            column.index
          )
        }
      }
      column.answers = newAnswersForColumn
      setColumns(newColumns)
      if (onSelect) {
        onSelect(newColumns, choices)
      }
    }

    // Utility to apply drag results on an array (for internal reordering).
    const applyDrag = <T,>(arr: T[], dragResult: DropResult<T>): T[] => {
      const { removedIndex, addedIndex, payload } = dragResult
      if (removedIndex === null && addedIndex === null) return arr
      const result = [...arr]
      let itemToAdd = payload
      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0]
      }
      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd)
      }
      return result
    }

    // Check answers: mark errors, display feedback, and mark the quiz as checked.
    const checkAnswers = () => {
      setAreColumnAnswersChecked(true)
      const updatedColumns = columns.map((column) => {
        const updatedAnswers = column.answers.map((choice) => ({
          ...choice,
          error: choice.userColumn !== choice.column,
        }))
        return { ...column, answers: updatedAnswers }
      })
      setColumns(updatedColumns)

      // Calculate counts
      const wrong = updatedColumns.reduce(
        (sum, col) => sum + col.answers.filter((choice) => choice.error).length,
        0
      )
      const correct = updatedColumns.reduce(
        (sum, col) =>
          sum + col.answers.filter((choice) => !choice.error).length,
        0
      )

      setCorrectCount(correct)
      setWrongCount(wrong)

      if (onAllAnswered && wrong + correct == totalQuestions)
        onAllAnswered(totalQuestions, correct, wrong)

      const allColumnsCorrect = updatedColumns.every((column) =>
        column.answers.every((choice) => choice.userColumn === choice.column)
      )

      if ((minCorrect && minCorrect === correct) || allColumnsCorrect) {
        if (onCorrect) onCorrect(correct)
        setFeedbackMessage('')
      } else {
        if (onIncorrect) onIncorrect(wrong)
        // Check if there is a matching feedback for the wrong count.
        if (task.feedback && Array.isArray(task.feedback)) {
          const feedbackObj = task.feedback.find(
            (fb: { wrong: number; text: string }) => fb.wrong === wrongCount
          )
          if (feedbackObj) {
            setFeedbackMessage(feedbackObj.text)
          } else {
            setFeedbackMessage('')
          }
        }
      }
      setIsChecked(true)
    }

    // Reset the quiz to its initial state.
    const resetQuiz = () => {
      const { columns: newColumns, availableChoices: newChoices } =
        initializeColumnsAndChoices(task)
      setColumns(newColumns)
      setChoices(newChoices)
      setCurrentQuestion(0)
      setFeedbackMessage('')
      setIsChecked(false)
      setAreColumnAnswersChecked(false)
    }

    // Render a column drop zone.
    const renderColumn = (column: DraggableColumn, index: number) => (
      <Paper
        className='card-paper'
        key={column.index}
        sx={{
          p: 2,
          minHeight: 50,
          transition: 'transform 0.2s, box-shadow 0.2s',
          transform: hoveredColumn === index ? 'scale(1.05)' : 'none',
          boxShadow:
            hoveredColumn === index
              ? '0px 4px 20px rgba(0, 0, 0, 0.3)'
              : 'none',
          backgroundColor: '#275a86',
          borderRadius: '20px',
          textAlign: 'center',
          color: 'white',
        }}
        onClick={() => {
          if (isMobile) columnChangeMobile(index)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setHoveredColumn(index)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setHoveredColumn(null)
        }}
        onDrop={(e) => {
          setHoveredColumn(null)
          handleNativeDrop(index, e)
        }}
      >
        <Typography variant='h5' gutterBottom style={{ color: 'white' }}>
          {column.title}
        </Typography>
        <hr style={{ color: 'white', background: 'white' }} />
        <Container
          groupName='choices-group'
          onDrop={(result: DropResult<DraggableChoice>) =>
            columnChangeDesktop(index, {
              ...result,
              payload: { ...result.payload, from: 'column' },
            })
          }
          getChildPayload={(i: number) => column.answers[i]}
          style={{
            minHeight: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {column.answers.map(
            (choice: DraggableChoice, choiceIndex: number) => (
              <Draggable key={choice.id}>
                <Paper
                  onClick={() => {
                    if (isMobile && !isChecked && !selectedChoice) {
                      setSelectedColumnChoice((prev) =>
                        prev?.columnIndex === index &&
                        prev?.choiceIndex === choiceIndex
                          ? null
                          : { columnIndex: index, choiceIndex }
                      )
                    }
                  }}
                  style={{
                    cursor: 'grab',
                    color: choice.error ? 'white' : 'black',
                  }}
                  sx={{
                    p: 1,
                    my: 1,
                    backgroundColor: isChecked
                      ? choice.error
                        ? '#AB2328'
                        : 'green'
                      : 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderRadius: '20px',
                    padding: '6px 12px',
                    border:
                      isMobile &&
                      !isChecked &&
                      selectedColumnChoice?.columnIndex === index &&
                      selectedColumnChoice?.choiceIndex === choiceIndex
                        ? '2px solid yellow'
                        : 'none',
                  }}
                >
                  {choice.text}
                </Paper>
              </Draggable>
            )
          )}
        </Container>
      </Paper>
    )

    const columnSizes = {
      xs: 6, // Full width on mobile
      sm: 6,
      md: columns.length <= 2 ? 6 : 4, // 2 columns on medium screens if <= 2 columns, otherwise 3
      lg: columns.length <= 2 ? 6 : 4,
    }

    return (
      <Box
        className='columnTask-CONTAINER'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Carousel of available answers */}
        {/* If no choices are left, show Check Answers button */}
        <Box
          className='columnTask-STATEMENT'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1, // Allow it to expand dynamically
            minHeight: 'auto', // Ensure height adjusts with content
          }}
        >
          <Typography fontSize={16} align='center'>
            {isChecked
              ? wrongCount === 0
                ? `${t('all_correct')}, ${correctCount} ${t(
                    'out_of'
                  )} ${totalQuestions}! 🎉`
                : `${correctCount} ${t('out_of')} ${totalQuestions} ${t(
                    'correct'
                  ).toLowerCase()}`
              : processedStatements === -1
              ? `${t('all_statements_processed')}`
              : `${t('statement')} ${processedStatements} ${t(
                  'out_of'
                )} ${totalQuestions}`}
          </Typography>
        </Box>
        {choices.length === 0 && !isChecked ? (
          <Box
            className='columnTask-CHECK-ANSWERS'
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingY='16px'
          >
            <Button variant='contained' onClick={checkAnswers}>
              {t('check_answers')}
            </Button>
          </Box>
        ) : isChecked ? (
          <Box
            className='columnTask-REDO'
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingY='16px'
          >
            <Button variant='contained' onClick={resetQuiz}>
              {t('redo')}
            </Button>
          </Box>
        ) : (
          <Box
            className='columnTask-QUESTION-CONTAINER'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              flexGrow: 1, // Allow it to expand dynamically
              minHeight: 'auto', // Ensure height adjusts with content
            }}
          >
            <Carousel
              className='columnTask-CAROUSEL'
              index={currentQuestion}
              onChange={(newIndex) => setCurrentQuestion(newIndex)}
              autoPlay={false}
              sx={{
                overflowY: 'visible',
                overflowX: 'clip',
                width: '100%',
                flexGrow: 1, // Allow it to expand properly
              }}
              swipe={false}
              navButtonsAlwaysInvisible
              indicatorContainerProps={{
                style: { display: 'none' },
              }}
            >
              {choices.map((choice: DraggableChoice) => (
                <Box
                  className='columnTask-ANSWERS'
                  key={choice.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingBlock: '16px',
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      cursor: isMobile ? 'pointer' : 'grab',
                      outline:
                        selectedChoice?.id === choice.id
                          ? '2px solid yellow'
                          : 'none',
                      boxShadow:
                        selectedChoice?.id === choice.id
                          ? 'inset 0px 0px 0px 2px rgba(0,0,0,0.2)'
                          : 'inset 0px 0px 0px 2px rgba(0,0,0,0.2)',
                      textAlign: 'center',
                      userSelect: 'none',
                      backgroundColor: '#5A8EBB',
                      color: 'white',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      pointerEvents: !selectedChoice ? 'auto' : 'none',
                      maxWidth: '98%',
                    }}
                    draggable
                    onClick={() => {
                      if (isMobile && selectedColumnChoice) return
                      if (isMobile && !selectedChoice) {
                        setSelectedChoice(choice)
                      }
                    }}
                    onDragStart={(e) => {
                      if (!isMobile) {
                        e.dataTransfer.setData('choice', JSON.stringify(choice))
                      }
                    }}
                  >
                    <Typography
                      variant='body1'
                      style={{ color: 'white', fontSize: '16px' }}
                    >
                      {choice.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Carousel>
          </Box>
        )}

        {/* Columns */}
        <Grid
          container
          spacing={2}
          justifyContent='center'
          className='columnTask-GRID-COLUMNS'
        >
          {columns.map((column, index) => (
            <Grid item key={index} {...columnSizes}>
              {renderColumn(column, index)}
            </Grid>
          ))}
        </Grid>

        {/* Feedback message display under the columns */}
        {feedbackMessage && (
          <Box display='flex' justifyContent='center' sx={{ mt: 2 }}>
            <Typography variant='body1' color='error'>
              {feedbackMessage}
            </Typography>
          </Box>
        )}
      </Box>
    )
  })
)
